import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Home, Pause, Play, RotateCcw, SkipBack, SkipForward } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import {
  getLiveSessionById,
  sendLiveRuntimeCommand,
  subscribeLiveRuntimeStateBySessionId,
  subscribeLiveSessionById
} from '../services/liveSessionService'
import {
  LIVE_REMOTE_ENABLED,
  LIVE_REMOTE_HEARTBEAT_STALE_MS
} from '../config/liveFeatures'
import './TeacherLiveRemote.css'

function toTsMs(value) {
  return value?.toDate?.()?.getTime?.() ?? 0
}

function TeacherLiveRemote() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const { user, userData } = useAuth()
  const [session, setSession] = useState(null)
  const [runtimeState, setRuntimeState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState('')
  const [error, setError] = useState('')
  const [lastSentSeq, setLastSentSeq] = useState(0)
  const [playbackOverride, setPlaybackOverride] = useState(null)
  const [timerDurationSec, setTimerDurationSec] = useState(120)
  const [timerDurationInput, setTimerDurationInput] = useState('120')
  const [nowMs, setNowMs] = useState(Date.now())
  const lastSentByTypeRef = useRef({})
  /** Évite d'écraser la saisie quand Firestore renvoie la durée (sinon impossible de vider le champ). */
  const editingTimerDurationRef = useRef(false)

  useEffect(() => {
    if (!user || userData?.role !== 'teacher') {
      navigate('/dashboard')
      return
    }
    if (!LIVE_REMOTE_ENABLED) {
      setError('La télécommande mobile est désactivée pour le moment.')
      setLoading(false)
      return
    }
    if (!sessionId) {
      setError('Session live introuvable.')
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    getLiveSessionById(sessionId)
      .then((data) => {
        if (cancelled) return
        if (!data) {
          setError('Session live introuvable.')
          setLoading(false)
          return
        }
        if (String(data.teacherId || '') !== String(user.uid || '')) {
          setError('Session non autorisée.')
          setLoading(false)
          return
        }
        setSession(data)
        const d = Math.max(10, Number(data?.timer?.durationSec || 120))
        setTimerDurationSec(d)
        setTimerDurationInput(String(d))
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err?.message || 'Impossible de charger la session live.')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [sessionId, user, userData?.role, navigate])

  useEffect(() => {
    if (!sessionId || !user?.uid || String(session?.teacherId || '') !== String(user.uid || '')) return () => {}
    const unsubscribe = subscribeLiveSessionById(
      sessionId,
      (data) => {
        setSession(data || null)
        if (data?.timer?.durationSec && !editingTimerDurationRef.current) {
          const d = Math.max(10, Number(data.timer.durationSec))
          setTimerDurationSec(d)
          setTimerDurationInput(String(d))
        }
      },
      () => {}
    )
    return () => unsubscribe()
  }, [sessionId, user?.uid, session?.teacherId])

  useEffect(() => {
    if (!sessionId || !user?.uid || String(session?.teacherId || '') !== String(user.uid || '')) return () => {}
    const unsubscribe = subscribeLiveRuntimeStateBySessionId(
      sessionId,
      (data) => setRuntimeState(data || null),
      () => {}
    )
    return () => unsubscribe()
  }, [sessionId, user?.uid, session?.teacherId])

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 5000)
    return () => clearInterval(id)
  }, [])

  const projectionHeartbeatMs = useMemo(
    () => toTsMs(runtimeState?.projection?.heartbeatAt),
    [runtimeState?.projection?.heartbeatAt]
  )
  const projectionOnline = projectionHeartbeatMs > 0 && (nowMs - projectionHeartbeatMs) <= LIVE_REMOTE_HEARTBEAT_STALE_MS
  const projectionPlaybackState = useMemo(() => {
    const expectedSeq = Number(playbackOverride?.expectedSeq || 0)
    const lastHandledSeq = Number(runtimeState?.telemetry?.lastHandledSeq || 0)
    if (expectedSeq > 0 && lastHandledSeq < expectedSeq && (playbackOverride?.state === 'playing' || playbackOverride?.state === 'paused')) {
      return playbackOverride.state
    }
    const explicit = runtimeState?.projection?.isPlaying
    if (explicit === true) return 'playing'
    if (explicit === false) return 'paused'
    return 'unknown'
  }, [playbackOverride?.expectedSeq, playbackOverride?.state, runtimeState?.projection?.isPlaying, runtimeState?.telemetry?.lastHandledSeq])

  useEffect(() => {
    if (!playbackOverride) return
    const expectedSeq = Number(playbackOverride.expectedSeq || 0)
    const lastHandledSeq = Number(runtimeState?.telemetry?.lastHandledSeq || 0)
    if (expectedSeq > 0 && lastHandledSeq >= expectedSeq) {
      setPlaybackOverride(null)
    }
  }, [playbackOverride, runtimeState?.telemetry?.lastHandledSeq])

  const sendCommand = async (type, payload = {}, throttleMs = 0) => {
    if (!session?.id || !user?.uid) return
    const now = Date.now()
    const prevSent = Number(lastSentByTypeRef.current[type] || 0)
    if (throttleMs > 0 && (now - prevSent) < throttleMs) return
    lastSentByTypeRef.current[type] = now
    setSending(type)
    setError('')
    try {
      const result = await sendLiveRuntimeCommand(session.id, user.uid, { type, payload })
      const seq = Number(result?.seq || 0)
      setLastSentSeq(seq)
      if (type === 'play' || type === 'pause') {
        setPlaybackOverride({ expectedSeq: seq, state: type === 'play' ? 'playing' : 'paused' })
        setRuntimeState((prev) => {
          if (!prev || typeof prev !== 'object') {
            return { projection: { isPlaying: type === 'play' } }
          }
          return {
            ...prev,
            projection: {
              ...(prev.projection || {}),
              isPlaying: type === 'play'
            }
          }
        })
      }
    } catch (err) {
      setError(err?.message || 'Commande impossible.')
    } finally {
      setSending('')
    }
  }

  if (!user || userData?.role !== 'teacher') return null

  return (
    <div className="teacher-live-remote-page">
      <header className="teacher-live-remote-header">
        <button type="button" className="teacher-live-remote-back" onClick={() => navigate('/dashboard/live-sessions')}>
          ← Retour sessions live
        </button>
        <h1>Télécommande mobile</h1>
      </header>

      {error && <p className="teacher-live-remote-error">{error}</p>}

      {loading ? (
        <div className="teacher-live-remote-loading"><div className="spinner" /><p>Chargement…</p></div>
      ) : (
        <>
          <section className="teacher-live-remote-card">
            <h2>État projection</h2>
            <p>
              <strong>{projectionOnline ? 'Projection en ligne' : 'Projection hors ligne'}</strong>
            </p>
            <p className="teacher-live-remote-muted">
              Dernier heartbeat: {projectionHeartbeatMs > 0 ? new Date(projectionHeartbeatMs).toLocaleTimeString('fr-FR') : '—'}
            </p>
            <p className="teacher-live-remote-muted">Dernière commande seq: {lastSentSeq || '—'}</p>
          </section>

          <section className="teacher-live-remote-card">
            <h2>Navigation vidéo</h2>
            <div className="teacher-live-remote-nav">
              <button type="button" className="nav-btn" disabled={sending !== ''} onClick={() => sendCommand('seek_start', {}, 250)}>
                <Home size={18} aria-hidden />
                <span>Début</span>
              </button>
              <button type="button" className="nav-btn" disabled={sending !== ''} onClick={() => sendCommand('marker_prev', {}, 250)}>
                <SkipBack size={18} aria-hidden />
                <span>Marqueur précédent</span>
              </button>
              <button
                type="button"
                className="nav-btn is-primary"
                disabled={sending !== ''}
                onClick={() => {
                  const nextType = projectionPlaybackState === 'playing' ? 'pause' : 'play'
                  sendCommand(nextType)
                }}
              >
                {projectionPlaybackState === 'playing' ? <Pause size={18} aria-hidden /> : <Play size={18} aria-hidden />}
                <span>
                  {projectionPlaybackState === 'playing'
                    ? 'Mettre en pause'
                    : projectionPlaybackState === 'paused'
                      ? 'Lecture'
                      : 'Lecture / Pause'}
                </span>
              </button>
              <button type="button" className="nav-btn" disabled={sending !== ''} onClick={() => sendCommand('marker_next', {}, 250)}>
                <SkipForward size={18} aria-hidden />
                <span>Marqueur suivant</span>
              </button>
            </div>
          </section>

          <section className="teacher-live-remote-card">
            <h2>Session</h2>
            <div className="teacher-live-remote-grid">
              <button type="button" disabled={sending !== ''} onClick={() => sendCommand('show_correction')}>Afficher correction</button>
              <button type="button" disabled={sending !== ''} onClick={() => sendCommand('prev_exercise', {}, 250)}>Exercice précédent</button>
              <button type="button" disabled={sending !== ''} onClick={() => sendCommand('next_exercise', {}, 250)}>Exercice suivant</button>
            </div>
          </section>

          <section className="teacher-live-remote-card">
            <h2>Timer</h2>
            <div className="teacher-live-remote-grid">
              <button type="button" disabled={sending !== ''} onClick={() => sendCommand('timer_start')}>Start</button>
              <button type="button" disabled={sending !== ''} onClick={() => sendCommand('timer_pause')}>Pause</button>
              <button type="button" disabled={sending !== ''} onClick={() => sendCommand('timer_reset')}>
                <RotateCcw size={16} aria-hidden />
                <span>Reset</span>
              </button>
              <button type="button" disabled={sending !== ''} onClick={() => sendCommand('timer_hide')}>Masquer</button>
            </div>
            <p className="teacher-live-remote-presets-label">Durées rapides</p>
            <div className="teacher-live-remote-presets">
              {[
                { sec: 30, label: '30 s' },
                { sec: 60, label: '1 min' },
                { sec: 120, label: '2 min' }
              ].map(({ sec, label }) => (
                <button
                  key={sec}
                  type="button"
                  disabled={sending !== ''}
                  className={timerDurationSec === sec ? 'is-active' : ''}
                  onClick={() => {
                    editingTimerDurationRef.current = false
                    const d = Math.max(10, sec)
                    setTimerDurationSec(d)
                    setTimerDurationInput(String(d))
                    sendCommand('timer_set_duration', { durationSec: d }, 350)
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="teacher-live-remote-duration">
              <label htmlFor="remote-duration">Durée (sec)</label>
              <input
                id="remote-duration"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                placeholder="10–900"
                value={timerDurationInput}
                onFocus={() => {
                  editingTimerDurationRef.current = true
                }}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, '').slice(0, 4)
                  setTimerDurationInput(next)
                }}
                onBlur={() => {
                  const raw = timerDurationInput.replace(/\D/g, '')
                  if (raw === '') {
                    setTimerDurationInput(String(timerDurationSec))
                    editingTimerDurationRef.current = false
                    return
                  }
                  const n = parseInt(raw, 10)
                  const clamped = Math.max(10, Math.min(900, n))
                  setTimerDurationSec(clamped)
                  setTimerDurationInput(String(clamped))
                  editingTimerDurationRef.current = false
                }}
              />
              <button
                type="button"
                disabled={sending !== ''}
                onClick={() => {
                  const raw = timerDurationInput.replace(/\D/g, '')
                  const n = parseInt(raw, 10)
                  const d = Number.isFinite(n) ? Math.max(10, Math.min(900, n)) : timerDurationSec
                  editingTimerDurationRef.current = false
                  setTimerDurationSec(d)
                  setTimerDurationInput(String(d))
                  sendCommand('timer_set_duration', { durationSec: d }, 350)
                }}
              >
                Appliquer
              </button>
            </div>
          </section>

        </>
      )}
    </div>
  )
}

export default TeacherLiveRemote
